import {
  blank,
  fromCells,
  plot,
  row,
  stamp,
  thickLine,
  toCells,
  type Cells,
} from '../compose';
import { HORSE_SLOTS } from '../palettes';
import { TRANSPARENT, type SpriteDef } from '../types';

/**
 * The horse. 56×40, facing right, anchor on the ground line. Frames are
 * composed: one torso, limbs rasterized from per-frame joint poses
 * (Muybridge transverse gallop, 1878 plates as public-domain reference
 * — see CREDITS.md), then mane and tail streamed per frame and a light
 * pass run over the finished silhouette.
 *
 * Slots: 0 outline · 1 coatDark · 2 coatMid · 3 coatLight · 4 maneDark ·
 * 5 maneLight · 6 hoof · 7 sock · 8 eye · 9 muzzle · a coatHi.
 */

const W = 56;
const H = 40;

const COAT_DARK = '1';
const COAT_MID = '2';
const COAT_LIGHT = '3';
const COAT_HI = 'a';
const OUTLINE = '0';

// The torso: rump → dipped back → withers → crest → head. The tail is
// no longer baked in here; it streams per frame.
const TORSO: string[] = [
  row(W, [45, '0'], [48, '0']), // ear tips
  row(W, [44, '00400']), // ears + forelock
  row(W, [41, '444'], [44, '022220']), // crest mane, skull top
  row(W, [39, '4455'], [43, '0'], [44, '222223'], [50, '0']),
  row(W, [37, '44555'], [42, '0'], [43, '222228'], [49, '23'], [51, '0']), // eye
  row(W, [35, '44455'], [40, '0'], [41, '2222222233'], [51, '00']), // nose bridge
  row(W, [33, '445'], [36, '333'], [39, '222222222222'], [51, '0999'], [55, '0']), // muzzle
  row(W, [14, '0000000'], [31, '000'], [34, '33333'], [39, '222222222222'], [51, '0999'], [55, '0']),
  row(W, [12, '03'], [14, '3333333'], [21, '0000000000'], [31, '333'], [34, '222222222222'], [46, '22222'], [51, '0989'], [55, '0']), // nostril
  row(W, [10, '0'], [11, '33333333333333333333'], [31, '22222222222222'], [45, '122222'], [51, '00000']), // jaw + mouth
  row(W, [10, '0'], [11, '3322222222'], [21, '22222222222222222222222'], [44, '12222'], [49, '00']), // throat
  row(W, [10, '0'], [11, '22222222222222222222222222222222'], [43, '1222'], [47, '0']),
  row(W, [10, '0'], [11, '2222222222222222222222222222222'], [42, '122'], [45, '0']),
  row(W, [10, '0'], [11, '2222222222222222222222222222222'], [42, '12'], [44, '0']),
  row(W, [10, '0'], [11, '222222222222222222222222222222'], [41, '12'], [43, '0']),
  row(W, [11, '0'], [12, '22222222222222222222222222222'], [41, '1'], [42, '0']),
  row(W, [11, '0'], [12, '2222222222222222222222222222'], [40, '10']),
  row(W, [12, '0'], [13, '111'], [16, '222222222222222222222'], [37, '1110']),
  row(W, [12, '0'], [13, '1111'], [17, '222222222222222222'], [35, '11110']),
  row(W, [13, '0'], [14, '11111'], [19, '2222222222222'], [32, '11111110']),
  row(W, [14, '000000000000000000'], [32, '11111110']), // belly line
  row(W, [32, '1111110']), // chest keel
  row(W, [33, '00000']),
];

// ---------------------------------------------------------------- limbs

type Pt = readonly [number, number];

interface LegPose {
  /** Knee (foreleg) or hock (hind), fetlock, hoof — relative to attach. */
  joint: Pt;
  fetlock: Pt;
  hoof: Pt;
}

function pose(joint: Pt, fetlock: Pt, hoof: Pt): LegPose {
  return { joint, fetlock, hoof };
}

const segment = thickLine;

function drawLimb(cells: Cells, attach: Pt, legPose: LegPose, fill: string, sock: boolean): void {
  const joint: Pt = [attach[0] + legPose.joint[0], attach[1] + legPose.joint[1]];
  const fetlock: Pt = [attach[0] + legPose.fetlock[0], attach[1] + legPose.fetlock[1]];
  const hoof: Pt = [attach[0] + legPose.hoof[0], attach[1] + legPose.hoof[1]];
  // Outline pass: wider strokes underneath…
  segment(cells, attach, joint, 5, OUTLINE);
  segment(cells, joint, fetlock, 4, OUTLINE);
  segment(cells, fetlock, hoof, 4, OUTLINE);
  segment(cells, [hoof[0], hoof[1] + 1], [hoof[0], hoof[1] + 1], 4, OUTLINE);
  // …then the leg itself, thicker at the gaskin than the cannon.
  segment(cells, attach, joint, 3, fill);
  segment(cells, joint, fetlock, 2, fill);
  segment(cells, fetlock, hoof, 2, sock ? '7' : fill);
  segment(cells, hoof, [hoof[0], hoof[1] + 1], 2, '6');
}

/**
 * Twelve beats of transverse gallop. The cycle: lead hind lands (0),
 * the diagonal bears (1–4), push-off (5–7), gathered suspension (8–9),
 * then extension and the reach for the next stride (10–11).
 */
const FRONT_POSES: LegPose[] = [
  pose([6, 7], [11, 11], [15, 13]), // 0  thrown forward, reaching to land
  pose([5, 8], [10, 13], [13, 16]), // 1  reaching down
  pose([3, 8], [6, 14], [7, 18]), // 2  strike
  pose([1, 8], [2, 14], [2, 18]), // 3  bearing forward
  pose([0, 8], [-1, 14], [-1, 18]), // 4  full support, vertical
  pose([-2, 8], [-4, 14], [-5, 17]), // 5  rocking over
  pose([-3, 8], [-7, 13], [-10, 16]), // 6  push
  pose([-3, 7], [-8, 10], [-11, 11]), // 7  leaving the ground
  pose([-1, 7], [-6, 7], [-8, 4]), // 8  folded under (suspension)
  pose([2, 6], [0, 10], [-3, 9]), // 9  knee lifting
  pose([5, 6], [6, 11], [5, 14]), // 10 swinging through
  pose([6, 7], [9, 11], [11, 13]), // 11 swinging forward
];

const HIND_POSES: LegPose[] = [
  pose([2, 7], [4, 14], [5, 19]), // 0  contact
  pose([0, 8], [1, 14], [2, 19]), // 1  bearing
  pose([-2, 8], [-1, 14], [0, 19]), // 2  support
  pose([-4, 8], [-6, 13], [-6, 17]), // 3  drive
  pose([-5, 7], [-9, 11], [-11, 14]), // 4  driving out
  pose([-6, 7], [-11, 10], [-15, 12]), // 5  full extension behind
  pose([-5, 7], [-10, 11], [-14, 14]), // 6  trailing
  pose([-3, 6], [-8, 8], [-10, 6]), // 7  folding up
  pose([0, 6], [-4, 8], [-6, 6]), // 8  tucked (suspension)
  pose([2, 6], [1, 9], [0, 9]), // 9  swinging under
  pose([4, 6], [6, 11], [8, 14]), // 10 reaching under
  pose([3, 6], [5, 13], [6, 17]), // 11 reaching for the ground
];

export const GALLOP_FRAME_COUNT = 12;
/** Metres covered by one full cycle — the renderer's frame clock. */
export const GALLOP_STRIDE_M = 4.5;
export const GALLOP_FRAMES_PER_M = GALLOP_FRAME_COUNT / GALLOP_STRIDE_M;

const FRONT_ATTACH: Pt = [37, 18];
const HIND_ATTACH: Pt = [15, 17];
/** Far legs run a quarter-cycle behind the near pair, set back and in. */
const FAR_PHASE = 3;
const FAR_OFFSET: Pt = [-3, 0];

/** Gathered suspension floats highest; the bearing stride sits. Exported
 * so the jockey rides in lockstep with the horse under him. */
export const BOB = [0, 0, -1, -1, -1, -1, -2, -2, -3, -3, -2, -1];

// ------------------------------------------------------- mane and tail

/**
 * The tail as one swept mass rather than radiating strands — strands
 * read as spikes at this size. It streams a beat behind the stride,
 * lifting through suspension and settling on the drive.
 */
const TAIL: string[] = [
  row(10, [6, '4455']),
  row(10, [2, '4455445']),
  row(10, [0, '445544555']),
  row(10, [1, '4554455']),
  row(10, [3, '45544']),
  row(10, [5, '44']),
];

/** Vertical lag per frame — the tail floats up as the horse gathers. */
const TAIL_FLOW = [1, 1, 0, 0, 0, 0, -1, -1, -1, -1, 0, 0];

function tailOffset(index: number): number {
  return TAIL_FLOW[index % TAIL_FLOW.length] ?? 0;
}

// ------------------------------------------------------------ lighting

/**
 * A light pass over the finished silhouette rather than hand-painted
 * highlights: the sun sits high and slightly behind, so the topmost coat
 * pixel of every column takes a rim and the lowest takes the shade.
 * Doing it programmatically means every pose gets lit consistently.
 */
function lightPass(cells: Cells): void {
  const height = cells.length;
  const width = cells[0]?.length ?? 0;
  for (let x = 0; x < width; x += 1) {
    for (let y = 0; y < height; y += 1) {
      const cell = cells[y]![x]!;
      if (cell === TRANSPARENT || cell === OUTLINE) continue;
      if (cell === COAT_MID || cell === COAT_LIGHT) cells[y]![x] = COAT_HI;
      break;
    }
  }
}

/** Muscle: the crease where the haunch meets the barrel, and the line of
 * the shoulder. Two strokes that give the flat body some structure. */
function drawMuscle(cells: Cells, yOffset: number): void {
  const shade = (from: Pt, to: Pt): void => {
    const steps = Math.max(Math.abs(to[0] - from[0]), Math.abs(to[1] - from[1]), 1);
    for (let step = 0; step <= steps; step += 1) {
      const t = step / steps;
      const x = Math.round(from[0] + (to[0] - from[0]) * t);
      const y = Math.round(from[1] + (to[1] - from[1]) * t);
      if (cells[y]?.[x] === COAT_MID) plot(cells, x, y, COAT_DARK);
    }
  };
  // Haunch crease, rump into flank.
  shade([21, 12 + yOffset], [23, 18 + yOffset]);
  // Shoulder, running down from the withers.
  shade([34, 12 + yOffset], [32, 18 + yOffset]);
}

// -------------------------------------------------------------- frames

function buildFrame(
  front: LegPose,
  frontFar: LegPose,
  hind: LegPose,
  hindFar: LegPose,
  bob: number,
  tailLift: number,
): string[] {
  const cells = toCells(blank(W, H));
  const shift = (p: Pt, extra: Pt): Pt => [p[0] + extra[0], p[1] + extra[1] + bob];
  // Far side first, in shadow.
  drawLimb(cells, shift(HIND_ATTACH, FAR_OFFSET), hindFar, COAT_DARK, false);
  drawLimb(cells, shift(FRONT_ATTACH, FAR_OFFSET), frontFar, COAT_DARK, false);
  // The tail hangs behind everything.
  // x=2 so the dock overlaps the rump; the torso then draws over it.
  const withTail = stamp(fromCells(cells), TAIL, 2, 10 + bob + tailLift);
  const withTorso = toCells(stamp(withTail, TORSO, 0, 2 + bob));
  drawMuscle(withTorso, bob);
  drawLimb(withTorso, shift(HIND_ATTACH, [0, 0]), hind, COAT_MID, true);
  drawLimb(withTorso, shift(FRONT_ATTACH, [0, 0]), front, COAT_MID, true);
  lightPass(withTorso);
  return fromCells(withTorso);
}

function gallopFrame(index: number): string[] {
  return buildFrame(
    FRONT_POSES[index]!,
    FRONT_POSES[(index + FAR_PHASE) % GALLOP_FRAME_COUNT]!,
    HIND_POSES[index]!,
    HIND_POSES[(index + FAR_PHASE) % GALLOP_FRAME_COUNT]!,
    BOB[index]!,
    tailOffset(index),
  );
}

const STAND_FRONT = pose([0, 8], [0, 14], [0, 18]);
const STAND_FRONT_FAR = pose([1, 8], [1, 14], [1, 18]);
const STAND_HIND = pose([-2, 8], [-1, 14], [0, 19]);
const STAND_HIND_FAR = pose([-1, 8], [0, 14], [1, 19]);

function idleFrame(breath: number): string[] {
  const cells = toCells(blank(W, H));
  drawLimb(cells, [HIND_ATTACH[0] - 3, HIND_ATTACH[1] + 1], STAND_HIND_FAR, COAT_DARK, false);
  drawLimb(cells, [FRONT_ATTACH[0] - 3, FRONT_ATTACH[1] + 1], STAND_FRONT_FAR, COAT_DARK, false);
  // At rest the tail hangs, barely swinging.
  const withTail = stamp(fromCells(cells), TAIL, 2, 11 + breath);
  const withTorso = toCells(stamp(withTail, TORSO, 0, 3 + breath));
  drawMuscle(withTorso, 1 + breath);
  drawLimb(withTorso, [HIND_ATTACH[0], HIND_ATTACH[1] + 1], STAND_HIND, COAT_MID, true);
  drawLimb(withTorso, [FRONT_ATTACH[0], FRONT_ATTACH[1] + 1], STAND_FRONT, COAT_MID, true);
  lightPass(withTorso);
  return fromCells(withTorso);
}

export const HORSE_GALLOP: SpriteDef = {
  name: 'horse-gallop',
  w: W,
  h: H,
  paletteSlots: [...HORSE_SLOTS],
  frames: Array.from({ length: GALLOP_FRAME_COUNT }, (_, index) => gallopFrame(index)),
  anchor: [28, 38],
};

export const HORSE_IDLE: SpriteDef = {
  name: 'horse-idle',
  w: W,
  h: H,
  paletteSlots: [...HORSE_SLOTS],
  frames: [idleFrame(0), idleFrame(1)],
  anchor: [28, 38],
  fps: 1.6,
};

/** All legs folded — the drill's airborne pose. */
export const HORSE_TUCK: SpriteDef = {
  name: 'horse-tuck',
  w: W,
  h: H,
  paletteSlots: [...HORSE_SLOTS],
  frames: [buildFrame(FRONT_POSES[8]!, FRONT_POSES[8]!, HIND_POSES[8]!, HIND_POSES[8]!, -2, -2)],
  anchor: [28, 38],
};
